// Amplitude analytics wrapper. One init at app mount; typed helpers per
// spec event. All helpers are no-ops until init() succeeds with a non-empty
// API key, so forgetting to configure it in dev won't break anything.

import * as amplitude from '@amplitude/analytics-browser';
import { AMPLITUDE_API_KEY } from '../config/analytics.config';

export type Platform = 'iOS' | 'AND' | 'web';

export type AgentName = 'Mental Health' | 'Nutrition' | 'Content Suggestion' | 'Support';

export type TaskProps = {
  type: 'content' | 'breath' | 'WellnessPass' | 'quote' | '1-1Guidance';
  contentType?: 'meditation' | 'story' | 'music' | 'sound' | 'blog' | null;
  contentID?: string | null;
  taskTitle: string;
  time: 'morning' | 'evening' | 'afternoon' | 'anytime';
};

let ready = false;
// Amplitude requires a user id on every event. We block every track call
// until setUser() is invoked with a non-empty id — this guarantees events
// are never sent anonymously.
let hasUserId = false;
let soulPageOpenedFired = false;
let soulChatStartFired = false;
let baseProps: Record<string, unknown> = {};

function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'web';
  const hasToken = new URLSearchParams(window.location.search).has('token');
  if (!hasToken) return 'web';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'AND';
  return 'web';
}

const platform: Platform = detectPlatform();
baseProps = { platform };

export function getPlatform(): Platform {
  return platform;
}

export function initAnalytics(): void {
  if (ready || !AMPLITUDE_API_KEY) return;
  try {
    amplitude.init(AMPLITUDE_API_KEY, { autocapture: false });
    ready = true;
  } catch (err) {
    // Never let analytics break the app
    console.warn('[analytics] init failed', err);
  }
}

export function setUser(
  userId: string,
  props?: { companyName?: string | null }
): void {
  if (!ready || !userId) return;
  try {
    amplitude.setUserId(userId);
    baseProps = {
      ...baseProps,
      userID: userId,
      'B2B Company': props?.companyName ?? null,
    };
    const userProperties = new amplitude.Identify();
    userProperties.set('userID', userId);
    userProperties.set('B2B Company', props?.companyName ?? '');
    amplitude.identify(userProperties);
    hasUserId = true;
  } catch (err) {
    console.warn('[analytics] setUser failed', err);
  }
}

function track(name: string, props: Record<string, unknown> = {}): void {
  // Never emit events without a known user — Amplitude requires user_id.
  if (!ready || !hasUserId) return;
  try {
    amplitude.track(name, { ...baseProps, ...props });
  } catch (err) {
    console.warn('[analytics] track failed', name, err);
  }
}

// ─── Event helpers ────────────────────────────────────────────────────────

export function trackSoulPageOpened(): void {
  if (platform !== 'web') return; // spec: webde atılıyor, appte atılmıyor
  if (soulPageOpenedFired) return;
  if (!ready || !hasUserId) return; // wait until user is identified
  soulPageOpenedFired = true;
  track('Soul Page Opened');
}

// Fired once when the (empty) chat screen opens. Web-only, mirrors
// Soul Page Opened: waits until the user is identified, guarded so it never
// double-fires within a session.
export function trackSoulChatStart(): void {
  if (platform !== 'web') return; // spec: web-only, not fired in the app
  if (soulChatStartFired) return;
  if (!ready || !hasUserId) return; // wait until user is identified
  soulChatStartFired = true;
  track('Soul Chat Start');
}

export function trackAgentOpened(name: AgentName): void {
  track('Agent Opened', { name });
}

export function trackActionsPageOpen(): void {
  track('Page Open', { type: 'Actions' });
}

export function trackSoulMessageSent(
  type: AgentName,
  extra?: {
    agentType?: string | null;
    conversationId?: string | null;
    turnNumber?: number | null;
    messageLength?: number | null;
  }
): void {
  track('Soul Message Sent', {
    type,
    agentType: extra?.agentType ?? null,
    ConversationID: extra?.conversationId ?? null,
    turnNumber: extra?.turnNumber ?? null,
    messageLength: extra?.messageLength ?? null,
  });
}

export function trackTaskOpened(props: TaskProps): void {
  track('Task Opened', props as unknown as Record<string, unknown>);
}

export function trackTaskCompleted(props: TaskProps): void {
  track('Task Completed', props as unknown as Record<string, unknown>);
}

export type ConversationButton =
  | 'end conversation'
  | 'content suggestion'
  | '1-1 guidance'
  | 'insight';

export function trackConversationButton(type: ConversationButton): void {
  track('Button Click', { where: 'conversation', type });
}

export type SidebarButton = 'insights' | 'action renewal' | 'dark mode' | 'light mode';

export function trackSidebarButton(type: SidebarButton): void {
  track('Button Click', { where: 'side bar', type });
}

export function trackOldConversationViewed(date: string | null): void {
  track('Message Viewed', {
    where: 'side bar',
    type: 'old conversation',
    date: date || null,
  });
}

export type ApproachType = 'solution focused' | 'reflective';

// Fires a single event listing every option the user was offered.
// Properties look like `option 1: solution focused`, `option 2: reflective`.
export function trackOptionOffered(options: ApproachType[]): void {
  const props: Record<string, unknown> = { where: 'Mental Health' };
  options.forEach((opt, idx) => {
    props[`option ${idx + 1}`] = opt;
  });
  track('Option Offered', props);
}

export function trackOptionSelected(type: ApproachType): void {
  track('Option Selected', { where: 'Mental Health', type });
}

export type ContentType = 'meditation' | 'story' | 'music' | 'sound' | 'blog';

export function trackContentSuggested(
  contentType: ContentType | null | undefined,
  contentID: string | null | undefined
): void {
  track('Content Suggested', {
    contentType: contentType || null,
    contentID: contentID || null,
  });
}

export function trackContentClicked(
  contentType: ContentType | null | undefined,
  contentID: string | null | undefined
): void {
  track('Content Clicked', {
    contentType: contentType || null,
    contentID: contentID || null,
  });
}

export function trackSoulSatisfaction(
  satisfactionRate: 1 | 2 | 3 | 4 | 5,
  extra?: { agentType?: string | null }
): void {
  track('Soul Satisfaction', {
    satisfactionRate,
    agentType: extra?.agentType ?? null,
  });
}

// Fired once when a conversation reaches a natural close (end-conversation
// summary). agentType is the conversation's primary (most-used) agent.
// satisfactionScore is usually unknown here (rating is collected separately
// via Soul Satisfaction) — included for spec completeness when available.
export function trackSoulConversationCompleted(props: {
  totalTurns: number;
  durationSeconds: number;
  agentType: string | null;
  satisfactionScore?: number | null;
}): void {
  track('Soul Conversation Completed', {
    totalTurns: props.totalTurns,
    durationSeconds: props.durationSeconds,
    agentType: props.agentType,
    satisfactionScore: props.satisfactionScore ?? null,
  });
}

// Fired when the user leaves an in-progress conversation without it reaching a
// natural close (page unload). turnNumber = which turn they dropped at, so we
// can see WHERE conversations are abandoned (early drop = UX problem).
export function trackSoulConversationAbandoned(props: {
  turnNumber: number;
  agentType: string | null;
  durationSeconds: number;
}): void {
  track('Soul Conversation Abandoned', {
    turnNumber: props.turnNumber,
    agentType: props.agentType,
    durationSeconds: props.durationSeconds,
  });
}

// Most frequent meaningful agent id across a conversation's assistant turns.
// Ignores empty/`general` so the dominant real agent wins; null if none.
export function derivePrimaryAgent(
  agentIds: Array<string | null | undefined>
): string | null {
  const counts = new Map<string, number>();
  for (const a of agentIds) {
    if (!a || a === 'general') continue;
    counts.set(a, (counts.get(a) || 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  for (const [a, n] of counts) {
    if (n > bestN) {
      best = a;
      bestN = n;
    }
  }
  return best;
}

// ─── Mapping helpers ──────────────────────────────────────────────────────

export function mapAgentIdToName(agentId: string | null | undefined): AgentName | null {
  switch (agentId) {
    case 'mental-health':
      return 'Mental Health';
    case 'nutrition':
      return 'Nutrition';
    case 'content-recommendation':
      return 'Content Suggestion';
    case 'faq':
    case 'features': // merged into FAQ
      return 'Support';
    default:
      return null;
  }
}

export function mapTimeCategoryToTime(
  timeCategory: string | null | undefined
): TaskProps['time'] {
  switch (timeCategory) {
    case 'sabah':
      return 'morning';
    case 'ogle':
      return 'afternoon';
    case 'aksam':
      return 'evening';
    default:
      return 'anytime';
  }
}

export function mapContentCategoryToContentType(
  contentCategory: string | null | undefined
): TaskProps['contentType'] {
  switch (contentCategory) {
    case 'meditation':
    case 'sleep_meditation':
      return 'meditation';
    case 'story':
      return 'story';
    case 'music':
      return 'music';
    case 'theme':
    case 'nature':
      return 'sound';
    case 'blog':
    case 'talk':
      return 'blog';
    default:
      return null;
  }
}

// Decide the spec-level `type` for a task. Order matters:
// 1) breathing   → breath
// 2) session     → 1-1Guidance
// 3) explicit wellnesspass deeplink → WellnessPass
// 4) quote-like  → quote
// 5) default     → content
export function mapTaskType(task: {
  type?: string | null;
  deeplink?: string | null;
  contentCategory?: string | null;
}): TaskProps['type'] {
  const t = (task.type || '').toLowerCase();
  const dl = (task.deeplink || '').toLowerCase();
  if (t === 'breathing' || dl.includes('/startbreathexercise')) return 'breath';
  if (t === 'session') return '1-1Guidance';
  if (dl.includes('wellnesspass-app.meditopia.com') || dl.includes('/wellnesspass')) return 'WellnessPass';
  if (t === 'quote') return 'quote';
  return 'content';
}
